/**
 * ProceduralAtlasExtras — shared canvas helpers, palette constants, and the
 * "extra" procedural textures that aren't entity bodies: FX bullets/impacts,
 * polish drop-shadows / portal auras, the tactical background tile, the
 * fullscreen vignette + scanline overlay, decorative map landmarks, and
 * top-down squad sprites for the four archetypes.
 *
 * Co-located with ProceduralAtlas.ts so both halves of the procedural-asset
 * pipeline share the same palette + Canvas 2D primitives. Splitting keeps
 * each file under the 600-line cap.
 */

import type Phaser from 'phaser';

export type Ctx = CanvasRenderingContext2D;

export const TWO_PI = Math.PI * 2;

// V1.8 facelift — atlas super-sampling. Entity textures bake at this multiple
// of their nominal frame size so that when the camera zooms in past 1× the
// GPU has enough source pixels to stay sharp (instead of bilinearly stretching
// 1-px source detail across many display pixels = soft / "pixelated" look).
//   • Adds ~10 MB of GPU texture memory total across all entity atlases.
//   • Disk / bundle size unchanged (textures are still procedural at boot).
//   • Drawing code below uses NOMINAL coordinates; each gen function calls
//     ctx.scale(ATLAS_SCALE, ATLAS_SCALE) once to expand into the bigger
//     canvas. Frame regions registered via tex.add use SCALED pixel coords.
//   • Atlases that are inherently soft (shadows, portals, light pools) or
//     tiled at world scale (bg, obstacles) deliberately skip the super-
//     sampling — diminishing returns + memory bloat for no visible gain.
export const ATLAS_SCALE = 3;

// Palette — shared with ProceduralAtlas.ts and Renderer.ts constants.
// V1.6 readability pass — player squads brighter saturated blue. The original
// (#3a7bd5) was too dim against the blue hex map. Cyan (#00d4ff) merged with
// the Core's accents. Lime green looked wrong against the sci-fi palette.
// This is a brighter, more saturated royal blue: distinct from the muted
// background, distinct from the Core's cyan, and reads cleanly against the
// enemy red.
export const C_P0 = '#4a96ff';
// V1.8 facelift — bright variants pushed slightly more saturated. The base
// hues stayed (extensively iterated for readability) but the highlight tones
// were reading pastel against the dark plates; a small chroma bump makes
// halos feel neon rather than washed.
export const C_P0_BRIGHT = '#9ecaff';
export const C_P1 = '#d55a3a';
export const C_P1_BRIGHT = '#ffa377';
export const C_NEUTRAL = '#5a6470';
export const C_TEAL = '#00d4aa';
export const C_GOLD = '#e8c830';
export const C_DAMAGED = '#8c2a2a';
export const C_DAMAGED_BRIGHT = '#ff4040';
export const C_DARK = '#0e1218';
export const C_PLATE = '#1a2028';
export const C_PLATE_LIGHT = '#28323e';
export const C_PANEL = '#141822';
export const C_HEADING = '#eef3f8';
export const C_OUTLINE = '#0b0d10';

// ---------------------------------------------------------------------------
// Canvas helpers (re-used by ProceduralAtlas + this file)
// ---------------------------------------------------------------------------

export function circ(
  ctx: Ctx, cx: number, cy: number, r: number,
  fill: string | null, stroke: string | null = null, lw = 1.5,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TWO_PI);
  if (fill !== null) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke !== null) { ctx.strokeStyle = stroke; ctx.lineWidth = lw; ctx.stroke(); }
}

export function hexPath(ctx: Ctx, cx: number, cy: number, r: number, rot = -Math.PI / 6): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3 + rot;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export function octPath(ctx: Ctx, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4 + Math.PI / 8;
    ctx[i === 0 ? 'moveTo' : 'lineTo'](cx + r * Math.cos(a), cy + r * Math.sin(a));
  }
  ctx.closePath();
}

export function halo(ctx: Ctx, cx: number, cy: number, r: number, color: string, alpha = 0.5): void {
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  g.addColorStop(0, hexA(color, alpha));
  g.addColorStop(0.55, hexA(color, alpha * 0.35));
  g.addColorStop(1, hexA(color, 0));
  ctx.fillStyle = g;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
}

export function bevelFill(ctx: Ctx, x: number, y: number, w: number, h: number, base: string, hi: string): void {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, hi);
  g.addColorStop(0.4, base);
  g.addColorStop(1, C_DARK);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

export function hexA(hex: string, a: number): string {
  const h = hex.startsWith('#') && hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export function makeCanvas(scene: Phaser.Scene, key: string, fw: number, fh: number, count: number): Phaser.Textures.CanvasTexture {
  const tex = scene.textures.createCanvas(key, fw * count, fh);
  if (tex === null) {
    throw new Error(`Atlas: createCanvas('${key}') returned null (size ${fw * count}×${fh}).`);
  }
  return tex;
}

// ---------------------------------------------------------------------------
// Public entry point — called from ProceduralAtlas.generate()
// ---------------------------------------------------------------------------

export function generateExtras(scene: Phaser.Scene): void {
  genFx(scene);
  genPolish(scene);
  genLightPools(scene);
  genBackgroundTile(scene);
  genVignette(scene);
  genScanlines(scene);
  genLandmarks(scene);
  genObstacle(scene);
}

// ---------------------------------------------------------------------------
// Light pools + capture rings — dedicated SQUARE atlas. Lives in its own
// texture because the polish atlas is 200×120 per frame (built for shadows
// and elongated portals); a radius-90 circular pool clipped to 120px tall
// produces visible horizontal edges at the frame boundary when the sprite
// is displayed at near-square sizes. Square 220×220 frame fits the full
// circle and lets the additive sprite stay correctly round at any display
// aspect ratio.
// ---------------------------------------------------------------------------
function genLightPools(scene: Phaser.Scene): void {
  const FW = 220, FH = 220;
  const names = ['pool_p0', 'pool_p1', 'ring_p0', 'ring_p1'];
  const tex = makeCanvas(scene, 'pools', FW, FH, names.length);
  const ctx = tex.context;
  names.forEach((name, i) => {
    const cx = i * FW + FW / 2, cy = FH / 2;
    ctx.save();
    if (name === 'pool_p0' || name === 'pool_p1') {
      const color = name === 'pool_p0' ? C_P0_BRIGHT : C_P1_BRIGHT;
      const pg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 100);
      pg.addColorStop(0,    hexA(color, 0.55));
      pg.addColorStop(0.35, hexA(color, 0.28));
      pg.addColorStop(0.7,  hexA(color, 0.10));
      pg.addColorStop(1,    hexA(color, 0));
      ctx.fillStyle = pg;
      ctx.beginPath(); ctx.arc(cx, cy, 100, 0, TWO_PI); ctx.fill();
    } else {
      // ring_p0 / ring_p1 — thin captured-territory ring.
      const color = name === 'ring_p0' ? C_P0_BRIGHT : C_P1_BRIGHT;
      ctx.globalAlpha = 0.85;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(cx, cy, 60, 0, TWO_PI); ctx.stroke();
      ctx.globalAlpha = 0.45;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(cx, cy, 54, 0, TWO_PI); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  });
  tex.refresh();
  names.forEach((name, i) => tex.add(name, 0, i * FW, 0, FW, FH));
}

// ---------------------------------------------------------------------------
// FX — bullets, impacts, beams
// ---------------------------------------------------------------------------

function genFx(scene: Phaser.Scene): void {
  const FW = 64, FH = 64;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const names = [
    'bullet', 'hit_small', 'hit_large', 'hit_smoke', 'ring_impact', 'beam', 'ring_signal', 'targeting',
    // V1.7 cinematic FX pass — punchy combat impact set.
    'muzzle_flash',     // bright star-burst with cone for fire FX
    'smoke_puff',       // soft gray billow
    'spark',            // tiny additive bright dot for particle bursts
    'tracer_trail',     // elongated bullet trail streak
    'explosion_core',   // white-hot center used on building destroyed
    'explosion_ring',   // orange/red expanding shockwave ring
    'shell_casing',     // small brass capsule (rotated by emitter)
    'ember',            // ambient ember particle
    'dust_mote',        // ambient dust mote
  ];
  const tex = makeCanvas(scene, 'fx', FWS, FHS, names.length);
  const ctx = tex.context;
  ctx.scale(ATLAS_SCALE, ATLAS_SCALE);
  names.forEach((name, i) => {
    const cx = i * FW + FW / 2, cy = FH / 2;
    ctx.save();
    if (name === 'bullet') {
      // V1.8 facelift — COLOR-NEUTRAL tracer so SpriteLayer.setTint() can
      // produce clean team colours (cyan for P0, true orange for P1).
      // Previous version had a cyan/blue base that multiplied with the
      // orange tint to produce a dirty brown — the "weird golden footballs"
      // bug. All paint here is now white/near-white; team identity comes
      // entirely from setTint at render time.
      // Outer additive bloom — wide soft white halo.
      ctx.globalAlpha = 0.45;
      const bloom = ctx.createLinearGradient(cx - 30, cy, cx + 30, cy);
      bloom.addColorStop(0,    'rgba(255,255,255,0)');
      bloom.addColorStop(0.35, 'rgba(255,255,255,0.55)');
      bloom.addColorStop(0.85, 'rgba(255,255,255,0.95)');
      bloom.addColorStop(1,    'rgba(255,255,255,0)');
      ctx.fillStyle = bloom;
      ctx.beginPath(); ctx.ellipse(cx, cy, 30, 8, 0, 0, TWO_PI); ctx.fill();
      // Inner streak — thinner, brighter.
      ctx.globalAlpha = 1;
      const streak = ctx.createLinearGradient(cx - 26, cy, cx + 26, cy);
      streak.addColorStop(0,    'rgba(255,255,255,0)');
      streak.addColorStop(0.55, 'rgba(255,255,255,0.85)');
      streak.addColorStop(1,    'rgba(255,255,255,1)');
      ctx.fillStyle = streak;
      ctx.beginPath(); ctx.ellipse(cx, cy, 26, 3.5, 0, 0, TWO_PI); ctx.fill();
      // Hot tip — pure white so tint passes through clean.
      ctx.beginPath(); ctx.ellipse(cx + 10, cy, 12, 2, 0, 0, TWO_PI);
      ctx.fillStyle = '#ffffff'; ctx.fill();
      // Tiny pinpoint highlight at the very front for a "spark" feel
      ctx.beginPath(); ctx.arc(cx + 20, cy, 1.4, 0, TWO_PI); ctx.fill();
    } else if (name === 'hit_small') {
      halo(ctx, cx, cy, 18, '#ffcc44', 0.6);
      ctx.strokeStyle = '#fff2a8'; ctx.lineWidth = 1.6; ctx.lineCap = 'round';
      for (let j = 0; j < 8; j++) {
        const a = (j * TWO_PI) / 8 + 0.2;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * 4, cy + Math.sin(a) * 4);
        ctx.lineTo(cx + Math.cos(a) * 14, cy + Math.sin(a) * 14);
        ctx.stroke();
      }
      circ(ctx, cx, cy, 3, '#ffffff');
    } else if (name === 'hit_large') {
      halo(ctx, cx, cy, 28, '#ff8a3a', 0.8);
      halo(ctx, cx, cy, 16, '#fff2a8', 0.95);
      ctx.strokeStyle = '#fff2a8'; ctx.lineWidth = 2; ctx.lineCap = 'round';
      for (let j = 0; j < 10; j++) {
        const a = (j * TWO_PI) / 10;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * 6, cy + Math.sin(a) * 6);
        ctx.lineTo(cx + Math.cos(a) * 24, cy + Math.sin(a) * 24);
        ctx.stroke();
      }
      circ(ctx, cx, cy, 6, '#ffffff');
    } else if (name === 'hit_smoke') {
      for (let j = 0; j < 6; j++) {
        const a = (j * TWO_PI) / 6;
        halo(ctx, cx + Math.cos(a) * 8, cy + Math.sin(a) * 8, 12, '#cfd6dd', 0.35);
      }
      halo(ctx, cx, cy, 14, '#cfd6dd', 0.4);
    } else if (name === 'ring_impact') {
      halo(ctx, cx, cy, 24, '#ffffff', 0.55);
      circ(ctx, cx, cy, 18, null, '#ffffff', 3);
      ctx.globalAlpha = 0.5; circ(ctx, cx, cy, 24, null, '#ffffff', 1.5);
      ctx.globalAlpha = 0.3; circ(ctx, cx, cy, 28, null, '#ffffff', 1);
    } else if (name === 'beam') {
      const g = ctx.createLinearGradient(0, cy - 2, 0, cy + 2);
      g.addColorStop(0, 'rgba(170,210,255,0)');
      g.addColorStop(0.5, 'rgba(220,240,255,1)');
      g.addColorStop(1, 'rgba(170,210,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(i * FW + 2, cy - 2, FW - 4, 4);
    } else if (name === 'ring_signal') {
      halo(ctx, cx, cy, 26, '#66aaff', 0.55);
      circ(ctx, cx, cy, 20, null, '#aad6ff', 2);
      ctx.globalAlpha = 0.45; circ(ctx, cx, cy, 26, null, '#aad6ff', 1);
    } else if (name === 'targeting') {
      ctx.strokeStyle = '#f5e76e'; ctx.lineWidth = 1.5; ctx.lineCap = 'round';
      const r = 18;
      for (let q = 0; q < 4; q++) {
        const sx = q & 1 ? 1 : -1; const sy = q & 2 ? 1 : -1;
        ctx.beginPath();
        ctx.moveTo(cx + sx * r, cy + sy * (r - 8));
        ctx.lineTo(cx + sx * r, cy + sy * r);
        ctx.lineTo(cx + sx * (r - 8), cy + sy * r);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(cx - 6, cy); ctx.lineTo(cx + 6, cy);
      ctx.moveTo(cx, cy - 6); ctx.lineTo(cx, cy + 6);
      ctx.stroke();
    } else if (name === 'muzzle_flash') {
      // Star-burst cone pointing +X. Bright white core, gold rim, radial rays.
      // Drawn with additive intent (consumer sets blend mode).
      halo(ctx, cx + 6, cy, 26, '#fff4c8', 0.95);
      halo(ctx, cx + 4, cy, 16, '#ffffff', 1);
      // Cone projecting forward
      const cone = ctx.createLinearGradient(cx, cy, cx + 28, cy);
      cone.addColorStop(0,    'rgba(255,240,180,0.95)');
      cone.addColorStop(0.4,  'rgba(255,210,120,0.7)');
      cone.addColorStop(1,    'rgba(255,180,80,0)');
      ctx.fillStyle = cone;
      ctx.beginPath();
      ctx.moveTo(cx + 2, cy - 11);
      ctx.lineTo(cx + 28, cy - 4);
      ctx.lineTo(cx + 28, cy + 4);
      ctx.lineTo(cx + 2, cy + 11);
      ctx.closePath();
      ctx.fill();
      // Star rays
      ctx.strokeStyle = 'rgba(255,250,220,0.95)';
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      for (let j = 0; j < 8; j++) {
        const a = (j * TWO_PI) / 8;
        const r1 = 3, r2 = j % 2 === 0 ? 22 : 12;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
        ctx.stroke();
      }
      // White-hot center
      circ(ctx, cx + 4, cy, 6, '#ffffff');
      circ(ctx, cx + 4, cy, 3, '#ffffff');
    } else if (name === 'smoke_puff') {
      // Soft layered cloud — works as smoke billow or dust ring particle.
      for (let j = 0; j < 5; j++) {
        const a = (j * TWO_PI) / 5;
        halo(ctx, cx + Math.cos(a) * 7, cy + Math.sin(a) * 7, 18, '#d6dce4', 0.5);
      }
      halo(ctx, cx, cy, 22, '#e6ecf2', 0.55);
    } else if (name === 'spark') {
      // Tiny bright additive dot with halo — workhorse particle.
      halo(ctx, cx, cy, 12, '#ffe7a0', 0.85);
      circ(ctx, cx, cy, 3, '#ffffff');
      circ(ctx, cx, cy, 1.4, '#ffffff');
    } else if (name === 'tracer_trail') {
      // V1.8 facelift — COLOR-NEUTRAL trail. Same fix as 'bullet': paint
      // white so setTint() can multiply cleanly into the team colour.
      const g = ctx.createLinearGradient(0, cy, FW, cy);
      g.addColorStop(0,    'rgba(255,255,255,0)');
      g.addColorStop(0.7,  'rgba(255,255,255,0.6)');
      g.addColorStop(1,    'rgba(255,255,255,0.95)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(cx, cy, FW / 2 - 1, 2.5, 0, 0, TWO_PI);
      ctx.fill();
    } else if (name === 'explosion_core') {
      // Multi-layered fireball: white core, gold mid, orange outer.
      halo(ctx, cx, cy, 30, '#ff8a3a', 0.95);
      halo(ctx, cx, cy, 20, '#ffd070', 1);
      halo(ctx, cx, cy, 12, '#ffffff', 1);
      // Inner debris flecks
      ctx.fillStyle = '#fff8d0';
      for (let j = 0; j < 10; j++) {
        const a = (j * TWO_PI) / 10 + 0.3;
        const r = 4 + (j % 3) * 3;
        const px = cx + Math.cos(a) * r;
        const py = cy + Math.sin(a) * r;
        ctx.beginPath(); ctx.arc(px, py, 1.3, 0, TWO_PI); ctx.fill();
      }
    } else if (name === 'explosion_ring') {
      // Expanding shockwave — bright thin ring with soft falloff.
      ctx.globalAlpha = 1;
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#ffb060';
      circ(ctx, cx, cy, 22, null, '#ffb060', 3);
      ctx.globalAlpha = 0.6;
      circ(ctx, cx, cy, 26, null, '#ff8a3a', 2);
      ctx.globalAlpha = 0.3;
      circ(ctx, cx, cy, 30, null, '#ffd070', 1);
      ctx.globalAlpha = 1;
    } else if (name === 'shell_casing') {
      // Brass capsule — small horizontal cylinder with rim-light.
      ctx.fillStyle = '#a87830';
      ctx.beginPath();
      ctx.ellipse(cx, cy, 5, 1.8, 0, 0, TWO_PI);
      ctx.fill();
      // Rim shine on top
      ctx.fillStyle = '#ffd890';
      ctx.beginPath();
      ctx.ellipse(cx, cy - 0.6, 4, 0.8, 0, 0, TWO_PI);
      ctx.fill();
      // Base nub
      ctx.fillStyle = '#5a3a18';
      ctx.beginPath();
      ctx.arc(cx - 4, cy, 1.4, 0, TWO_PI);
      ctx.fill();
    } else if (name === 'ember') {
      // Drifting orange ember with bloom — additive ambient particle.
      halo(ctx, cx, cy, 8, '#ff8030', 0.75);
      circ(ctx, cx, cy, 1.6, '#ffd28a');
    } else if (name === 'dust_mote') {
      // Faint dust speck — additive at low alpha.
      halo(ctx, cx, cy, 6, '#cfd6dd', 0.45);
      circ(ctx, cx, cy, 0.9, '#ffffff');
    }
    ctx.restore();
  });
  tex.refresh();
  names.forEach((name, i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

// ---------------------------------------------------------------------------
// Polish — drop shadows + portal auras + decals
// ---------------------------------------------------------------------------

function drawShadow(ctx: Ctx, cx: number, cy: number, rx: number, ry: number): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
  g.addColorStop(0,   'rgba(0,0,0,0.52)');
  g.addColorStop(0.45,'rgba(0,0,0,0.30)');
  g.addColorStop(0.75,'rgba(0,0,0,0.12)');
  g.addColorStop(1,   'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx, 0, TWO_PI);
  ctx.fill();
  ctx.restore();
}

function drawPortal(ctx: Ctx, cx: number, cy: number, color: string): void {
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 95);
  g.addColorStop(0, hexA(color, 0.55));
  g.addColorStop(0.35, hexA(color, 0.3));
  g.addColorStop(0.7, hexA(color, 0.12));
  g.addColorStop(1, hexA(color, 0));
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.ellipse(cx, cy, 95, 55, 0, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = color; ctx.lineWidth = 2;
  ctx.globalAlpha = 0.4; ctx.beginPath(); ctx.ellipse(cx, cy, 88, 50, 0, 0, TWO_PI); ctx.stroke();
  ctx.globalAlpha = 0.25; ctx.beginPath(); ctx.ellipse(cx, cy, 72, 40, 0, 0, TWO_PI); ctx.stroke();
  ctx.globalAlpha = 1;
}

function genPolish(scene: Phaser.Scene): void {
  const FW = 200, FH = 120;
  const names = [
    'shadow_sm', 'shadow_md', 'shadow_lg', 'portal_p0', 'portal_p1', 'explosion_dust', 'floor_p0', 'floor_ring',
    // V1.7 cinematic decals — persistent ground stamps + viewport vignette.
    'scorch_decal',     // dark scorch ring for explosions/building destroyed
    'blood_p0',         // blue energy splatter (faction 0 squad death)
    'blood_p1',         // orange energy splatter (faction 1 squad death)
    'damage_vignette',  // red edge vignette for viewer damage flash
  ];
  const tex = makeCanvas(scene, 'polish', FW, FH, names.length);
  const ctx = tex.context;
  names.forEach((name, i) => {
    const cx = i * FW + FW / 2, cy = FH / 2;
    ctx.save();
    if (name === 'shadow_sm') drawShadow(ctx, cx, cy, 40, 22);
    else if (name === 'shadow_md') drawShadow(ctx, cx, cy, 54, 30);
    else if (name === 'shadow_lg') drawShadow(ctx, cx, cy, 72, 40);
    else if (name === 'portal_p0') drawPortal(ctx, cx, cy, C_P0);
    else if (name === 'portal_p1') drawPortal(ctx, cx, cy, C_P1);
    else if (name === 'explosion_dust') {
      for (let j = 0; j < 8; j++) {
        const a = (j * TWO_PI) / 8;
        halo(ctx, cx + Math.cos(a) * 24, cy + Math.sin(a) * 14, 22, '#cfd6dd', 0.3);
      }
    } else if (name === 'floor_p0') {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 60);
      g.addColorStop(0, hexA(C_P0, 0.25));
      g.addColorStop(1, hexA(C_P0, 0));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.ellipse(cx, cy, 60, 30, 0, 0, TWO_PI); ctx.fill();
    } else if (name === 'floor_ring') {
      ctx.globalAlpha = 0.4; ctx.strokeStyle = C_HEADING; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.ellipse(cx, cy, 56, 28, 0, 0, TWO_PI); ctx.stroke();
    } else if (name === 'scorch_decal') {
      // Irregular scorch ring with charred core — multiple alpha layers for
      // organic edge. Drawn at depth -300 by EffectsLayer.
      ctx.globalAlpha = 0.55;
      const sg = ctx.createRadialGradient(cx, cy, 4, cx, cy, 56);
      sg.addColorStop(0, 'rgba(0,0,0,0.85)');
      sg.addColorStop(0.45, 'rgba(20,15,10,0.6)');
      sg.addColorStop(0.85, 'rgba(60,30,15,0.25)');
      sg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = sg;
      ctx.beginPath(); ctx.ellipse(cx, cy, 56, 36, 0, 0, TWO_PI); ctx.fill();
      ctx.globalAlpha = 0.85; ctx.fillStyle = '#08060a';
      ctx.beginPath(); ctx.ellipse(cx, cy, 22, 12, 0, 0, TWO_PI); ctx.fill();
      ctx.globalAlpha = 0.5; ctx.strokeStyle = '#000'; ctx.lineWidth = 1.2;
      for (let j = 0; j < 9; j++) {
        const a = (j * TWO_PI) / 9 + 0.15;
        const r1 = 12 + (j % 3) * 4, r2 = 32 + (j % 4) * 8;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1 * 0.65);
        ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2 * 0.65);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    } else if (name === 'blood_p0' || name === 'blood_p1') {
      // Sci-fi "ichor" splatter — bright additive glow plus dark stain.
      // Player 0 = cyan-blue, Player 1 = orange-red.
      const energy = name === 'blood_p0' ? C_P0_BRIGHT : C_P1_BRIGHT;
      const dark   = name === 'blood_p0' ? '#0c2238' : '#3a1208';
      // Dark stain underlay
      ctx.globalAlpha = 0.75;
      const sg = ctx.createRadialGradient(cx, cy, 2, cx, cy, 40);
      sg.addColorStop(0,    dark);
      sg.addColorStop(0.7,  hexA(dark, 0.45));
      sg.addColorStop(1,    hexA(dark, 0));
      ctx.fillStyle = sg;
      ctx.beginPath(); ctx.ellipse(cx, cy, 40, 26, 0, 0, TWO_PI); ctx.fill();
      // Bright energy core
      ctx.globalAlpha = 0.85;
      halo(ctx, cx, cy, 22, energy, 0.95);
      // Radial splatter droplets
      ctx.globalAlpha = 1;
      ctx.fillStyle = energy;
      for (let j = 0; j < 14; j++) {
        const a = (j * TWO_PI) / 14 + ((j * 13) % 7) * 0.07;
        const r = 18 + ((j * 17) % 22);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r * 0.7;
        const rad = 1.2 + ((j * 11) % 5) * 0.4;
        ctx.beginPath(); ctx.arc(x, y, rad, 0, TWO_PI); ctx.fill();
      }
      // White-hot pinpoint at center
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(cx, cy, 2, 0, TWO_PI); ctx.fill();
      ctx.globalAlpha = 1;
    } else if (name === 'damage_vignette') {
      // Red radial vignette — transparent center, deep red at edges. Drawn
      // fullscreen over the camera when the viewer's core takes a hit.
      const dg = ctx.createRadialGradient(cx, cy, 30, cx, cy, 100);
      dg.addColorStop(0,    'rgba(255,40,40,0)');
      dg.addColorStop(0.55, 'rgba(220,30,30,0.25)');
      dg.addColorStop(1,    'rgba(170,20,20,0.85)');
      ctx.fillStyle = dg;
      ctx.fillRect(i * FW, 0, FW, FH);
    }
    ctx.restore();
  });
  tex.refresh();
  names.forEach((name, i) => tex.add(name, 0, i * FW, 0, FW, FH));
}

// ---------------------------------------------------------------------------
// Background tile — dark deck plate with hex-grid + panel seams.
//
// Canvas dimensions are chosen so the hex lattice tiles seamlessly:
//   HR = 32 → vertical period = 3 × HR = 96 px → TH = 192 (exactly 2 periods)
//              horizontal period = HR × √3 ≈ 55.426 px → TW = 277 (≈ 5 periods,
//              off by only ~0.13 px per tile — visually imperceptible).
// ---------------------------------------------------------------------------

function genBackgroundTile(scene: Phaser.Scene): void {
  const HR = 32; // hex radius
  const HW = HR * Math.sqrt(3); // horizontal spacing ≈ 55.426
  const TW = Math.round(5 * HW); // ≈ 277 — 5 horizontal periods, seam < 0.14 px/tile
  const TH = 2 * 3 * HR;         // = 192 — exact 2 vertical periods, zero vertical seam
  const PANEL_SEAM = 3 * HR;     // = 96 — aligns panel grid with hex vertical period
  const tex = makeCanvas(scene, 'bg_tile', TW, TH, 1);
  const ctx = tex.context;
  // Base gradient: subtle darker centre → lighter corners gives the floor depth.
  // V1.8 facelift — widened luminance range (was #0f141b → #161c25, only 7 units).
  // Darker center + brighter corner = entities pop off the floor in the play zone.
  const base = ctx.createRadialGradient(TW / 2, TH / 2, 30, TW / 2, TH / 2, Math.max(TW, TH));
  base.addColorStop(0, '#0c1118');
  base.addColorStop(1, '#1a2230');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, TW, TH);
  // Procedural grain — small bright/dark dots
  for (let i = 0; i < 350; i++) {
    const x = Math.random() * TW, y = Math.random() * TH;
    const a = Math.random() * 0.06;
    ctx.fillStyle = Math.random() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
    ctx.fillRect(x, y, 1, 1);
  }
  // Hex lattice (subtle teal). Pointy-top hex with horizontal rows.
  ctx.strokeStyle = 'rgba(80,180,200,0.10)';
  ctx.lineWidth = 1;
  for (let row = -1; row * HR * 1.5 < TH + HR; row++) {
    for (let col = -1; col * HW < TW + HW; col++) {
      const cx = col * HW + (row % 2 === 0 ? 0 : HW / 2);
      const cy = row * HR * 1.5;
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = (k * Math.PI) / 3 + Math.PI / 6;
        const x = cx + Math.cos(a) * HR, y = cy + Math.sin(a) * HR;
        if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
    }
  }
  // Brighter accent grid (panel seams) — adds tactical map feel
  ctx.strokeStyle = 'rgba(120,180,220,0.06)';
  ctx.lineWidth = 1;
  for (let s = 0; s <= TW; s += PANEL_SEAM) {
    ctx.beginPath(); ctx.moveTo(s, 0); ctx.lineTo(s, TH); ctx.stroke();
  }
  for (let s = 0; s <= TH; s += PANEL_SEAM) {
    ctx.beginPath(); ctx.moveTo(0, s); ctx.lineTo(TW, s); ctx.stroke();
  }
  // Corner rivets
  ctx.fillStyle = 'rgba(160,180,200,0.18)';
  [[8, 8], [TW - 8, 8], [8, TH - 8], [TW - 8, TH - 8]].forEach(([x, y]) => {
    if (x !== undefined && y !== undefined) {
      ctx.beginPath(); ctx.arc(x, y, 1.5, 0, TWO_PI); ctx.fill();
    }
  });
  tex.refresh();
}

// ---------------------------------------------------------------------------
// Vignette overlay — radial gradient dark frame, drawn fullscreen
// ---------------------------------------------------------------------------

function genVignette(scene: Phaser.Scene): void {
  const W = 1024, H = 1024;
  const tex = makeCanvas(scene, 'vignette', W, H, 1);
  const ctx = tex.context;
  // V1.8 facelift — mid stop pulled inward + darker (0.55→0.45, 0.18→0.24) so
  // the falloff is steeper while corner darkness stays at 0.78. Effect: brighter
  // center-of-frame "hot zone" without making the edges pitch black.
  const g = ctx.createRadialGradient(W / 2, H / 2, W * 0.18, W / 2, H / 2, W * 0.62);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(0.45, 'rgba(0,0,0,0.24)');
  g.addColorStop(1, 'rgba(0,0,0,0.78)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  tex.refresh();
}

// ---------------------------------------------------------------------------
// Scanline overlay tile (4×4) — faint horizontal CRT lines for HUD feel
// ---------------------------------------------------------------------------

function genScanlines(scene: Phaser.Scene): void {
  const tex = makeCanvas(scene, 'scanlines', 4, 4, 1);
  const ctx = tex.context;
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.fillRect(0, 0, 4, 4);
  ctx.fillStyle = 'rgba(160,210,240,0.05)';
  ctx.fillRect(0, 0, 4, 1);
  ctx.fillStyle = 'rgba(0,0,0,0.10)';
  ctx.fillRect(0, 2, 4, 1);
  tex.refresh();
}

// ---------------------------------------------------------------------------
// Landmarks — decorative non-blocking world dressing
// ---------------------------------------------------------------------------

function drawPylon(ctx: Ctx, cx: number, cy: number): void {
  // V1.8 facelift — STRICTLY TOP-DOWN. Previous version was a side-view radio
  // tower (base at bottom, lattice cross-bracing up the middle, antenna spike
  // going up) — same perspective mismatch we fixed on beacons. The new pylon
  // is an antenna array seen from directly above: hexagonal armoured plate
  // with four cardinal antenna spires radiating outward, a central emitter
  // disc, and a faint cyan signal halo. Same visual family as relays.

  // 1) Soft cyan signal halo on the floor around the pylon.
  halo(ctx, cx, cy, 32, '#5fd0ff', 0.30);

  // 2) Outer hex armoured plate (full hex, not flattened).
  hexPath(ctx, cx, cy, 18);
  const plateGrad = ctx.createLinearGradient(cx - 16, cy - 16, cx + 16, cy + 16);
  plateGrad.addColorStop(0,   C_PLATE_LIGHT);
  plateGrad.addColorStop(0.5, C_PLATE);
  plateGrad.addColorStop(1,   C_DARK);
  ctx.fillStyle = plateGrad;
  ctx.fill();
  ctx.strokeStyle = C_OUTLINE;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  // Faint cyan accent rim
  ctx.strokeStyle = 'rgba(95,208,255,0.55)';
  ctx.lineWidth = 1;
  hexPath(ctx, cx, cy, 15);
  ctx.stroke();

  // 3) Four cardinal antenna spires radiating outward (NE, SE, SW, NW). Read
  // as antenna whips when viewed from above. Each spire ends with a tip nub.
  ctx.strokeStyle = '#7a8898';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  for (let k = 0; k < 4; k++) {
    const a = (k * Math.PI) / 2 + Math.PI / 4;
    const x1 = cx + Math.cos(a) * 18;
    const y1 = cy + Math.sin(a) * 18;
    const x2 = cx + Math.cos(a) * 28;
    const y2 = cy + Math.sin(a) * 28;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    // Tip nub
    ctx.fillStyle = '#5fd0ff';
    ctx.beginPath(); ctx.arc(x2, y2, 1.4, 0, TWO_PI); ctx.fill();
  }

  // 4) Hex vertex bolts (same language as relays + beacons).
  for (let k = 0; k < 6; k++) {
    const a = (k * Math.PI) / 3 - Math.PI / 6;
    const bx = cx + Math.cos(a) * 17;
    const by = cy + Math.sin(a) * 17;
    ctx.fillStyle = '#1a2028';
    ctx.beginPath(); ctx.arc(bx, by, 1.2, 0, TWO_PI); ctx.fill();
  }

  // 5) Inner darker hex recess.
  hexPath(ctx, cx, cy, 9);
  ctx.fillStyle = C_DARK;
  ctx.fill();
  ctx.strokeStyle = 'rgba(95,208,255,0.50)';
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // 6) Central emitter disc — cyan core with white-hot centre.
  ctx.fillStyle = '#5fd0ff';
  ctx.beginPath(); ctx.arc(cx, cy, 4.5, 0, TWO_PI); ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.arc(cx, cy, 1.8, 0, TWO_PI); ctx.fill();
}

function drawDebris(ctx: Ctx, cx: number, cy: number): void {
  // V1.8 facelift — torn-armor fragments. Previous version was 5 identical
  // rounded-rectangle polygons in flat gray; read as cardboard boxes rather
  // than battlefield debris. New version uses jagged 6-vertex outlines (each
  // chunk has its OWN vertex offsets so no two chunks are the same shape),
  // per-chunk bevel gradient (lit edge + shadow edge for 3D feel), and a
  // mix of clean steel + rust-tinted fragments.
  //
  // Each entry: [dx, dy, rot, scale, isRusted, vertexSeed]
  const chunks: Array<[number, number, number, number, boolean, number]> = [
    [-12,   8,  0.45,  1.10, false, 17],
    [ 10,  -4, -0.65,  0.95, true,  43],
    [ -2, -10,  0.20,  1.00, false, 91],
    [ 14,  12,  0.90,  0.85, true,  29],
    [-18,  -4,  1.10,  1.05, false, 71],
    [  4,   2, -0.30,  0.70, false, 53],
  ];
  for (const [dx, dy, rot, sc, rusted, seed] of chunks) {
    ctx.save();
    ctx.translate(cx + dx, cy + dy);
    ctx.rotate(rot);
    ctx.scale(sc, sc);
    // Deterministic pseudo-random vertex offsets so each chunk has a
    // unique torn-metal outline but stays consistent across reloads.
    const rng = (n: number): number => {
      const x = Math.sin(seed * 9301 + n * 49297) * 43758.5453;
      return x - Math.floor(x);
    };
    // 7-vertex jagged outline around a roughly 16×10 footprint.
    const baseVerts: Array<[number, number]> = [
      [-9, -3], [-5, -5], [ 3, -6], [ 8, -2],
      [ 7,  4], [ 0,  6], [-7,  4],
    ];
    ctx.beginPath();
    baseVerts.forEach(([vx, vy], i) => {
      const jx = (rng(i * 2) - 0.5) * 2.2;
      const jy = (rng(i * 2 + 1) - 0.5) * 1.8;
      if (i === 0) ctx.moveTo(vx + jx, vy + jy);
      else ctx.lineTo(vx + jx, vy + jy);
    });
    ctx.closePath();
    // Weathered bevel — duller than the original draft so debris looks
    // OLD and BEATEN UP, not polished and ready-to-ship. Compressed
    // luminance range, dirtier tones, rust colors muted toward brown
    // (less polished copper).
    const grad = ctx.createLinearGradient(-9, -6, 9, 6);
    if (rusted) {
      grad.addColorStop(0,    '#3e2818');
      grad.addColorStop(0.55, '#2a1c10');
      grad.addColorStop(1,    '#170c06');
    } else {
      grad.addColorStop(0,    '#3a414c');
      grad.addColorStop(0.55, '#262d36');
      grad.addColorStop(1,    '#13181e');
    }
    ctx.fillStyle = grad;
    ctx.fill();
    // Outline — slightly softer than crate outline so fragments don't
    // pop crisp against the background.
    ctx.strokeStyle = 'rgba(5,8,12,0.85)';
    ctx.lineWidth = 0.9;
    ctx.stroke();
    // Lit-edge highlight — alpha cut roughly in half (0.35 → 0.16) so the
    // chunks stop "shining." Reads as residual glint, not pristine bevel.
    ctx.strokeStyle = rusted ? 'rgba(180,130,80,0.18)' : 'rgba(220,225,235,0.16)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(baseVerts[0]![0], baseVerts[0]![1]);
    ctx.lineTo(baseVerts[1]![0], baseVerts[1]![1]);
    ctx.lineTo(baseVerts[2]![0], baseVerts[2]![1]);
    ctx.stroke();
    // Grime spots — 2–4 small dark blots distributed inside the chunk so
    // each fragment looks weathered, not lab-clean.
    const grimeCount = 2 + Math.floor(rng(200) * 3);
    for (let g = 0; g < grimeCount; g++) {
      const gx = (rng(g * 7 + 10) - 0.5) * 12;
      const gy = (rng(g * 7 + 11) - 0.5) * 8;
      const gr = 0.6 + rng(g * 7 + 12) * 1.0;
      ctx.fillStyle = `rgba(0,0,0,${0.22 + rng(g * 7 + 13) * 0.18})`;
      ctx.beginPath(); ctx.arc(gx, gy, gr, 0, TWO_PI); ctx.fill();
    }
    // Optional rust streak on weathered chunks — a thin warm smear across
    // the surface to break up the silhouette.
    if (rusted && rng(50) > 0.4) {
      ctx.strokeStyle = 'rgba(120,70,30,0.35)';
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(-6 + rng(60), -2);
      ctx.lineTo( 4 + rng(61),  3);
      ctx.stroke();
    }
    // A tiny rivet or bullet hole on roughly half the chunks (deterministic
    // by seed). Adds a "this was once part of something" feel.
    if (seed % 2 === 0) {
      ctx.fillStyle = '#05080c';
      ctx.beginPath(); ctx.arc(rng(99) * 8 - 4, rng(100) * 4 - 2, 0.8, 0, TWO_PI); ctx.fill();
    }
    ctx.restore();
  }
  // Scorch — soft gradient so it reads as a burn mark, not a hole
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, 14 / 24);
  const scorch = ctx.createRadialGradient(0, 0, 0, 0, 0, 24);
  scorch.addColorStop(0,   'rgba(0,0,0,0.30)');
  scorch.addColorStop(0.6, 'rgba(0,0,0,0.14)');
  scorch.addColorStop(1,   'rgba(0,0,0,0)');
  ctx.fillStyle = scorch;
  ctx.beginPath(); ctx.arc(0, 0, 24, 0, TWO_PI); ctx.fill();
  ctx.restore();
}

function drawBeacon(ctx: Ctx, cx: number, cy: number): void {
  // V1.8.3 — signal-marker silhouette. The pure-dot version read as a
  // collectible "coin" on the floor. New design adds an emitter RING around
  // the central spot so the beacon's silhouette is clearly "thing
  // broadcasting from the ground" rather than "circular pickup". The shape
  // is unique on the map: no other entity uses ring + centre dot at this
  // small scale, so beacons read instantly without competing with relays.

  // 1) Floor halo — soft warm glow on the ground.
  halo(ctx, cx, cy, 18, '#c8a060', 0.30);

  // 2) Outer emitter ring — thin warm amber, the "signal broadcast"
  // silhouette. Slightly broken (two small gaps at top + bottom) so it
  // reads as a marker symbol rather than a clean geometric ring.
  ctx.strokeStyle = 'rgba(216,176,96,0.70)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0.15 * Math.PI, 0.85 * Math.PI);   // top arc
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 1.15 * Math.PI, 1.85 * Math.PI);   // bottom arc
  ctx.stroke();

  // 3) Inner faint ring — adds depth without making the marker bright.
  ctx.strokeStyle = 'rgba(184,144,72,0.40)';
  ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.arc(cx, cy, 5, 0, TWO_PI); ctx.stroke();

  // 4) Central spot — small dim amber, plus a tiny warm pinpoint.
  ctx.fillStyle = '#a88040';
  ctx.beginPath(); ctx.arc(cx, cy, 2.6, 0, TWO_PI); ctx.fill();
  ctx.fillStyle = '#e8c080';
  ctx.beginPath(); ctx.arc(cx, cy, 1.2, 0, TWO_PI); ctx.fill();
}

function drawCrater(ctx: Ctx, cx: number, cy: number): void {
  // V1.8 facelift — top-down circular blast mark. Previous version stretched
  // BOTH the scorch ring AND the radial cracks via ctx.scale(1, 0.6), which
  // squished cracks into ellipse paths instead of straight rays.
  // Now: a slightly flattened scorch ellipse (subtle ground-projection cue,
  // matches shadow language) but the cracks are TRUE radial lines from the
  // crater centre, with varied lengths so the blast feels uneven.
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, 0.7);
  // Outer scorch — wide soft fade.
  const outerScorch = ctx.createRadialGradient(0, 0, 0, 0, 0, 40);
  outerScorch.addColorStop(0,    'rgba(20,12,6,0.45)');
  outerScorch.addColorStop(0.45, 'rgba(10,6,4,0.22)');
  outerScorch.addColorStop(1,    'rgba(0,0,0,0)');
  ctx.fillStyle = outerScorch;
  ctx.beginPath(); ctx.arc(0, 0, 40, 0, TWO_PI); ctx.fill();
  // Inner darker char — the deepest part of the impact.
  const innerChar = ctx.createRadialGradient(0, 0, 0, 0, 0, 14);
  innerChar.addColorStop(0,    'rgba(0,0,0,0.65)');
  innerChar.addColorStop(0.7,  'rgba(8,4,2,0.30)');
  innerChar.addColorStop(1,    'rgba(0,0,0,0)');
  ctx.fillStyle = innerChar;
  ctx.beginPath(); ctx.arc(0, 0, 14, 0, TWO_PI); ctx.fill();
  ctx.restore();
  // True radial cracks — straight rays from centre, varied lengths so the
  // blast looks uneven instead of perfectly symmetric. Each crack also has
  // a tiny "kink" partway out for organic feel.
  ctx.strokeStyle = 'rgba(60,80,100,0.45)';
  ctx.lineWidth = 1;
  ctx.lineCap = 'round';
  for (let i = 0; i < 7; i++) {
    const a = (i * TWO_PI) / 7 + ((i * 137) % 31) * 0.04;
    const inner = 5 + ((i * 13) % 4);
    const outer = 22 + ((i * 17) % 10);
    const kinkR = inner + (outer - inner) * 0.55;
    const kinkA = a + 0.12 * ((i & 1) ? 1 : -1);
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner,  Math.sin(a) * inner);
    ctx.lineTo(Math.cos(kinkA) * kinkR, Math.sin(kinkA) * kinkR);
    ctx.lineTo(Math.cos(a) * outer,  Math.sin(a) * outer);
    ctx.stroke();
  }
  // A handful of tiny shrapnel embers around the rim — bright orange-red
  // pinpoints suggesting residual heat.
  for (let i = 0; i < 4; i++) {
    const a = (i * TWO_PI) / 4 + 0.5;
    const r = 18 + ((i * 23) % 8);
    const ex = Math.cos(a) * r;
    const ey = Math.sin(a) * r;
    ctx.fillStyle = 'rgba(255,140,80,0.55)';
    ctx.beginPath(); ctx.arc(ex, ey, 0.7, 0, TWO_PI); ctx.fill();
  }
}

function genLandmarks(scene: Phaser.Scene): void {
  const FW = 80, FH = 80;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const names = ['pylon', 'debris', 'beacon', 'crater'];
  const tex = makeCanvas(scene, 'landmarks', FWS, FHS, names.length);
  const ctx = tex.context;
  ctx.scale(ATLAS_SCALE, ATLAS_SCALE);
  names.forEach((name, i) => {
    const cx = i * FW + FW / 2, cy = FH / 2;
    if (name === 'pylon') drawPylon(ctx, cx, cy);
    else if (name === 'debris') drawDebris(ctx, cx, cy);
    else if (name === 'beacon') drawBeacon(ctx, cx, cy);
    else if (name === 'crater') drawCrater(ctx, cx, cy);
  });
  tex.refresh();
  names.forEach((name, i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}


// ---------------------------------------------------------------------------
// Obstacle tile — bevelled metal crate (64×64), tiled by BackgroundLayer
// ---------------------------------------------------------------------------

function genObstacle(scene: Phaser.Scene): void {
  // V1.8 facelift — tileable armor-plate. Previous attempts swung between
  // "too loud" (cyan rim + yellow chevron + per-tile bolts) and "too quiet"
  // (flat slate with no texture). This version is the middle ground: an
  // armored cover plate with subtle rivet studs in a 16-px grid (tiles
  // perfectly — pattern continues seamlessly across tile boundaries) plus
  // procedural grain. The obstacle's outline + bevel (drawn by
  // BackgroundLayer.paintObstacles) provide the crate identity.
  const SZ = 64;
  const tex = makeCanvas(scene, 'obstacle_tile', SZ, SZ, 1);
  const ctx = tex.context;

  // Base plate — slightly lighter than the previous iteration so crates
  // have presence in vision but still sit visually below cores and relays.
  ctx.fillStyle = '#2c3340';
  ctx.fillRect(0, 0, SZ, SZ);

  // Subtle armor stud rivets — 4×4 grid (16-px spacing) per tile. Tiles
  // seamlessly because 64 / 16 = 4 (integer). Each rivet is a tiny bright
  // dot with an even tinier dark drop-shadow pip for fake 3D.
  for (let y = 8; y < SZ; y += 16) {
    for (let x = 8; x < SZ; x += 16) {
      // Shadow under the rivet (drawn first so it sits behind)
      ctx.fillStyle = 'rgba(0,0,0,0.30)';
      ctx.beginPath(); ctx.arc(x + 0.6, y + 0.7, 1.2, 0, TWO_PI); ctx.fill();
      // Rivet body — dark metal
      ctx.fillStyle = '#1a2028';
      ctx.beginPath(); ctx.arc(x, y, 1.4, 0, TWO_PI); ctx.fill();
      // Specular pip
      ctx.fillStyle = 'rgba(255,255,255,0.50)';
      ctx.beginPath(); ctx.arc(x - 0.4, y - 0.4, 0.7, 0, TWO_PI); ctx.fill();
    }
  }

  // Procedural noise grain — small bright/dark dots so the plate has surface
  // texture rather than a flat fill. Slightly more visible than the previous
  // pass (alpha cap 0.05 → 0.08).
  for (let i = 0; i < 200; i++) {
    const x = Math.random() * SZ;
    const y = Math.random() * SZ;
    const a = Math.random() * 0.08;
    ctx.fillStyle = Math.random() > 0.5 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
    ctx.fillRect(x, y, 1, 1);
  }

  tex.refresh();
}
